import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { CurrentPlanCard } from './CurrentPlanCard';
import { PlanSelector } from './PlanSelector';
import { InvoicesList } from './InvoicesList';
import { Skeleton } from '@/components/ui/skeleton';

interface BillingPanelProps {
  shopId?: string;
}

export const BillingPanel = ({ shopId }: BillingPanelProps) => {
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['billing-subscription', shopId],
    queryFn: async () => {
      if (!shopId) return null;

      const { data, error } = await supabase
        .from('billing_subscriptions')
        .select('*')
        .eq('shop_id', shopId)
        .in('status', ['active', 'trialing', 'past_due', 'scheduled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!shopId,
  });

  const { data: shopBetaInfo } = useQuery({
    queryKey: ['shop-beta-billing-info', shopId],
    queryFn: async () => {
      if (!shopId) return null;
      const { data, error } = await supabase
        .from('shops')
        .select('billing_enabled_at, is_beta_tester')
        .eq('id', shopId)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!shopId,
  });

  const billingEnabled = shopBetaInfo?.is_beta_tester
    ? !!shopBetaInfo?.billing_enabled_at
    : true;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <CurrentPlanCard subscription={subscription} shopId={shopId} />
      
      <Card className="p-6" data-testid="billing-plans-card">
        <h2 className="text-xl font-semibold mb-4">
          {subscription ? 'Change Plan' : 'Choose a Plan'}
        </h2>
        <PlanSelector currentPlanKey={subscription?.plan_key} shopId={shopId} billingEnabled={billingEnabled} isBetaTester={!!shopBetaInfo?.is_beta_tester} />
      </Card>

      {subscription && <InvoicesList shopId={shopId} />}
    </div>
  );
};
